import React, { useState } from 'react';
import {
  ChevronDown, ChevronUp, MapPin, Users, Clock, ClipboardCheck,
  Info, FileText, Plus, Sparkles, CheckCircle2, FileDown, Loader2,
  RefreshCw, Pencil, X, ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  briefApi, calendarApi, exportApi, projectsApi, transcriptsApi, workItemsApi,
} from '../api/services';
import type {
  CalendarEvent, AnnualStrategicDriver, Project, MeetingPrepResponse,
  MeetingTranscript, TranscriptExtraction, MeetingBrief, BriefThreadLink,
} from '../types/models';
import { MicrosoftIcon, GoogleIcon } from './icons/BrandIcons';
import DriverPill from './DriverPill';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { isAllowedThreadUrl } from '@/utils/emailLink';
import { formatProgress } from '../utils/drivers';
import { useEntitlements } from '../context/EntitlementsContext';

interface CalendarEventDetailProps {
  event: CalendarEvent | null;
  open: boolean;
  onClose: () => void;
  onDriverLinked: () => void;
  drivers: AnnualStrategicDriver[];
}

// Radix Select can't accept '' as a value; sentinel stands in for "no driver / no project".
const NONE = '__none__';

// Meeting Brief — the five prose sections, in render order.
type BriefSectionKey = 'whyItMatters' | 'openWithThem' | 'toDecide' | 'lastContact' | 'bring';
const BRIEF_SECTIONS: { key: BriefSectionKey; label: string }[] = [
  { key: 'whyItMatters', label: 'Why it matters' },
  { key: 'openWithThem', label: 'Open with them' },
  { key: 'toDecide', label: 'To decide' },
  { key: 'lastContact', label: 'Last contact' },
  { key: 'bring', label: 'Bring' },
];
const EMPTY_BRIEF_DRAFT: Record<BriefSectionKey, string> = {
  whyItMatters: '', openWithThem: '', toDecide: '', lastContact: '', bring: '',
};
const briefToDraft = (b: MeetingBrief): Record<BriefSectionKey, string> => ({
  whyItMatters: b.whyItMatters ?? '',
  openWithThem: b.openWithThem ?? '',
  toDecide: b.toDecide ?? '',
  lastContact: b.lastContact ?? '',
  bring: b.bring ?? '',
});

function briefStatusClass(status: string): string {
  switch (status) {
    case 'Ready': return 'border-emerald-500 text-emerald-600 dark:text-emerald-400';
    case 'Stale': return 'border-amber-500 text-amber-600 dark:text-amber-400';
    case 'Dismissed': return 'border-rose-500 text-rose-600 dark:text-rose-400';
    default: return 'border-slate-400 text-slate-500 dark:text-slate-300';
  }
}

function ThreadIcon({ kind }: { kind: BriefThreadLink['kind'] }) {
  // Outlook + Teams are Microsoft; Gmail is Google.
  return kind === 'Gmail'
    ? <GoogleIcon size={14} />
    : <MicrosoftIcon size={14} />;
}

function formatEventTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function borderToneClass(event: CalendarEvent): string {
  if (event.isPriorityMeeting) return 'border-l-rose-500';
  if (event.isPrivate) return 'border-l-amber-500';
  return 'border-l-primary';
}

function ragClass(rag?: string): string {
  if (rag === 'Green') return 'border-emerald-500 text-emerald-600 dark:text-emerald-400';
  if (rag === 'Amber') return 'border-amber-500 text-amber-600 dark:text-amber-400';
  if (rag === 'Red') return 'border-rose-500 text-rose-600 dark:text-rose-400';
  return '';
}

function ExpandToggle({ open, onClick, label }: { open: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-1.5 text-left"
    >
      <span className="text-sm font-semibold text-muted-foreground">{label}</span>
      {open
        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
        : <ChevronDown className="h-4 w-4 text-muted-foreground" />
      }
    </button>
  );
}

export default function CalendarEventDetail({
  event,
  open,
  onClose,
  onDriverLinked,
  drivers,
}: CalendarEventDetailProps) {
  // Driver and project links, transcript upload and the briefing pack only
  // show on plans that include them. Existing links are kept, just not shown.
  const { has } = useEntitlements();
  const driversIncluded = has('strategicDrivers');
  const projectsIncluded = has('projects');
  const transcriptsIncluded = has('transcriptProcessing');
  const exportIncluded = has('pdfExport');
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [applyToRecurring, setApplyToRecurring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [activeProjects, setActiveProjects] = useState<Project[]>([]);
  const [prepData, setPrepData] = useState<MeetingPrepResponse | null>(null);
  const [prepLoading, setPrepLoading] = useState(false);
  const [prepExpanded, setPrepExpanded] = useState(false);
  const [prepError, setPrepError] = useState<string | null>(null);
  const [brief, setBrief] = useState<MeetingBrief | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefGenerating, setBriefGenerating] = useState(false);
  const [briefLoaded, setBriefLoaded] = useState(false); // GET ran (brief may be null = none yet)
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);
  const [briefEditing, setBriefEditing] = useState(false);
  const [briefDraft, setBriefDraft] = useState<Record<BriefSectionKey, string>>(EMPTY_BRIEF_DRAFT);
  const [briefSaving, setBriefSaving] = useState(false);
  const [packDownloading, setPackDownloading] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [transcriptUploading, setTranscriptUploading] = useState(false);
  const [transcript, setTranscript] = useState<MeetingTranscript | null>(null);
  const [transcriptFeedback, setTranscriptFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [actionTitle, setActionTitle] = useState('');
  const [actionExpanded, setActionExpanded] = useState(false);
  const [actionCreating, setActionCreating] = useState(false);

  React.useEffect(() => {
    if (!projectsIncluded) { setActiveProjects([]); return; }
    projectsApi.getAll({ status: 'Active' }).then(setActiveProjects).catch(() => {});
  }, [projectsIncluded]);

  React.useEffect(() => {
    if (event) {
      setSelectedDriverId(event.strategicDriverId || '');
      setSelectedProjectId(event.projectId || '');
      setApplyToRecurring(false);
      setFeedback(null);
      setDescriptionExpanded(false);
      setPrepData(null);
      setPrepExpanded(false);
      setPrepError(null);
      setBrief(null);
      setBriefLoaded(false);
      setBriefExpanded(false);
      setBriefError(null);
      setBriefEditing(false);
      setBriefDraft(EMPTY_BRIEF_DRAFT);
      setTranscriptText('');
      setTranscriptExpanded(false);
      setTranscript(null);
      setTranscriptFeedback(null);
      setActionTitle('');
      setActionExpanded(false);
    }
  }, [event]);

  const handleDriverChange = async (driverIdRaw: string) => {
    if (!event) return;
    const driverId = driverIdRaw === NONE ? '' : driverIdRaw;
    setSelectedDriverId(driverId);
    setSaving(true);
    setFeedback(null);
    try {
      await calendarApi.linkToDriver(event.id, {
        strategicDriverId: driverId || null,
        applyToRecurring,
      });
      setFeedback({ type: 'success', text: 'Strategic driver updated successfully' });
      onDriverLinked();
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to link driver';
      setFeedback({
        type: 'error',
        text: typeof msg === 'string' ? msg : 'Failed to link driver',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProjectChange = async (projectIdRaw: string) => {
    if (!event) return;
    const projectId = projectIdRaw === NONE ? '' : projectIdRaw;
    setSelectedProjectId(projectId);
    setSaving(true);
    setFeedback(null);
    try {
      await calendarApi.linkToProject(event.id, {
        projectId: projectId || null,
      });
      setFeedback({ type: 'success', text: 'Project updated successfully' });
      onDriverLinked();
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to link project';
      setFeedback({
        type: 'error',
        text: typeof msg === 'string' ? msg : 'Failed to link project',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLoadMeetingPrep = async () => {
    if (!event) return;
    setPrepLoading(true);
    setPrepError(null);
    try {
      const data = await calendarApi.getMeetingPrep(event.id);
      setPrepData(data);
      setPrepExpanded(true);
    } catch (err: any) {
      setPrepError(err.message || 'Failed to load meeting prep');
    } finally {
      setPrepLoading(false);
    }
  };

  const handleLoadBrief = async () => {
    if (!event) return;
    setBriefLoading(true);
    setBriefError(null);
    try {
      const data = await briefApi.get(event.id);
      setBrief(data);
      setBriefDraft(briefToDraft(data));
      setBriefExpanded(true);
    } catch (err: any) {
      // 404 = no brief composed yet; offer to generate rather than error.
      if (err?.response?.status === 404) {
        setBrief(null);
        setBriefExpanded(true);
      } else {
        setBriefError(err?.response?.data?.error || err?.message || 'Failed to load brief');
      }
    } finally {
      setBriefLoaded(true);
      setBriefLoading(false);
    }
  };

  const handleGenerateBrief = async () => {
    if (!event) return;
    setBriefGenerating(true);
    setBriefError(null);
    try {
      const data = await briefApi.generate(event.id, { force: true });
      setBrief(data);
      setBriefDraft(briefToDraft(data));
      setBriefExpanded(true);
      setBriefLoaded(true);
    } catch (err: any) {
      setBriefError(err?.response?.data?.error || err?.message || 'Failed to generate brief');
    } finally {
      setBriefGenerating(false);
    }
  };

  const handleSaveBrief = async () => {
    if (!event) return;
    setBriefSaving(true);
    setBriefError(null);
    try {
      const data = await briefApi.patch(event.id, {
        whyItMatters: briefDraft.whyItMatters,
        openWithThem: briefDraft.openWithThem,
        toDecide: briefDraft.toDecide,
        lastContact: briefDraft.lastContact,
        bring: briefDraft.bring,
      });
      setBrief(data);
      setBriefDraft(briefToDraft(data));
      setBriefEditing(false);
    } catch (err: any) {
      setBriefError(err?.response?.data?.error || err?.message || 'Failed to save brief');
    } finally {
      setBriefSaving(false);
    }
  };

  const handleDismissBrief = async () => {
    if (!event) return;
    setBriefSaving(true);
    setBriefError(null);
    try {
      const data = await briefApi.patch(event.id, { dismiss: true });
      setBrief(data);
    } catch (err: any) {
      setBriefError(err?.response?.data?.error || err?.message || 'Failed to dismiss brief');
    } finally {
      setBriefSaving(false);
    }
  };

  const handleDownloadBriefingPack = async () => {
    if (!event) return;
    setPackDownloading(true);
    setPrepError(null);
    try {
      const response = await exportApi.downloadBriefingPackPdf(event.id);
      const blob = response.data as Blob;
      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const rfcMatch = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
      const legacyMatch = disposition?.match(/filename="?([^"]+)"?/i);
      const filename = rfcMatch?.[1]
        ? decodeURIComponent(rfcMatch[1].trim().replace(/"/g, ''))
        : legacyMatch?.[1] ?? `Cadence-BriefingPack-${event.id}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (err: any) {
      setPrepError(
        err?.response?.data?.error ??
        err?.message ??
        'Could not generate briefing pack PDF.',
      );
    } finally {
      setPackDownloading(false);
    }
  };

  const handleUploadAndAnalyse = async () => {
    if (!event || !transcriptText.trim()) return;
    setTranscriptUploading(true);
    setTranscriptFeedback(null);
    try {
      const meetingDate = format(new Date(event.startTime), 'yyyy-MM-dd');
      const uploaded = await transcriptsApi.upload({
        title: event.title,
        meetingDate,
        attendees: event.attendees || undefined,
        transcriptText: transcriptText.trim(),
        calendarEventId: event.id,
      });
      const result = await transcriptsApi.autoAccept(uploaded.id);
      setTranscript(result.transcript);
      setTranscriptText('');
      setTranscriptFeedback({
        type: 'success',
        text: `Analysed — ${result.workItemsCreated} action item${result.workItemsCreated !== 1 ? 's' : ''} created`,
      });
    } catch (err: any) {
      setTranscriptFeedback({ type: 'error', text: err.response?.data?.message || err.message || 'Failed to process transcript' });
    } finally {
      setTranscriptUploading(false);
    }
  };

  const handleCreateAction = async () => {
    if (!event || !actionTitle.trim()) return;
    setActionCreating(true);
    try {
      const meetingDate = format(new Date(event.startTime), 'yyyy-MM-dd');
      await workItemsApi.create({
        title: actionTitle.trim(),
        type: 'Task' as any,
        priority: 'High' as any,
        status: 'Backlog' as any,
        estimatedMinutes: 30,
        targetDate: meetingDate,
        strategicDriverId: (driversIncluded && event.strategicDriverId) || undefined,
        notes: `From meeting: ${event.title}`,
      });
      setActionTitle('');
      setTranscriptFeedback({ type: 'success', text: 'Action item created' });
    } catch (err: any) {
      setTranscriptFeedback({ type: 'error', text: err.message || 'Failed to create action' });
    } finally {
      setActionCreating(false);
    }
  };

  if (!event) return null;

  const isIcsSource = !!event.icsSource;
  const currentDriver = drivers.find((d) => d.id === event.strategicDriverId);
  const currentProject = activeProjects.find((p) => p.id === event.projectId);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className={cn('border-l-4 pl-3', borderToneClass(event))}>
            {event.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {event.isAllDay
                ? 'All day'
                : `${formatEventTime(event.startTime)} - ${formatEventTime(event.endTime)}`}
            </span>
          </div>

          {event.location && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{event.location}</span>
            </div>
          )}

          {event.attendees && (
            <div className="flex items-start gap-2">
              <Users className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <span className="whitespace-pre-line text-sm">{event.attendees}</span>
            </div>
          )}

          {event.description && (
            <div>
              <ExpandToggle
                open={descriptionExpanded}
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                label="Description"
              />
              {descriptionExpanded && (
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                  {event.description}
                </p>
              )}
            </div>
          )}

          {/* Strategic Driver */}
          {driversIncluded && (
            <div>
              <p className="mb-1 text-sm font-semibold text-muted-foreground">Strategic driver</p>
              <div className="mb-2">
                <DriverPill driver={currentDriver ?? null} />
              </div>
              <Select
                value={selectedDriverId || NONE}
                onValueChange={handleDriverChange}
                disabled={saving}
              >
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}><em>None</em></SelectItem>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>{driver.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <label className="mt-2 flex cursor-pointer items-center gap-2">
                <Checkbox
                  checked={applyToRecurring}
                  onCheckedChange={(v) => setApplyToRecurring(v === true)}
                />
                <span className="text-sm text-muted-foreground">Apply to all recurring instances</span>
              </label>

              {isIcsSource && (
                <div className="mt-2 flex items-start gap-1.5">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                  <p className="text-xs text-muted-foreground">
                    Link saved in Cadence. ICS calendars are read-only — the link won't appear in your external calendar.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Project */}
          {projectsIncluded && (
            <div>
              <p className="mb-1 text-sm font-semibold text-muted-foreground">Project</p>
              {currentProject ? (
                <Badge variant="outline" className="mb-2 border-primary text-primary">
                  {currentProject.title}
                </Badge>
              ) : (
                <Badge variant="outline" className="mb-2 text-muted-foreground">
                  No project linked
                </Badge>
              )}
              <Select
                value={selectedProjectId || NONE}
                onValueChange={handleProjectChange}
                disabled={saving}
              >
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}><em>None</em></SelectItem>
                  {activeProjects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>{project.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isIcsSource && (
                <div className="mt-2 flex items-start gap-1.5">
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
                  <p className="text-xs text-muted-foreground">
                    Link saved in Cadence. ICS calendars are read-only — the link won't appear in your external calendar.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Meeting Prep */}
          <div className="border-t border-border pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadMeetingPrep}
                disabled={prepLoading}
                className="gap-1.5"
              >
                {prepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
                {prepData ? 'Refresh Meeting Prep' : 'Meeting Prep'}
              </Button>
              {exportIncluded && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadBriefingPack}
                  disabled={packDownloading}
                  className="gap-1.5"
                >
                  {packDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                  Briefing pack PDF
                </Button>
              )}
              {prepData && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPrepExpanded(!prepExpanded)}
                >
                  {prepExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              )}
            </div>

            {prepError && (
              <Alert variant="error" className="mt-2">
                <AlertDescription>{prepError}</AlertDescription>
              </Alert>
            )}

            {prepExpanded && prepData && (
              <div className="mt-3 flex flex-col gap-3">
                {prepData.relatedWorkItems.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm font-semibold text-muted-foreground">
                      Related Work Items ({prepData.relatedWorkItems.length})
                    </p>
                    <ul className="flex flex-col gap-1">
                      {prepData.relatedWorkItems.map((wi) => (
                        <li key={wi.id} className="text-sm">
                          <p>{wi.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {wi.priority} | {wi.status} | {wi.estimatedMinutes}m
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {prepData.previousOccurrences.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm font-semibold text-muted-foreground">
                      Previous Meetings ({prepData.previousOccurrences.length})
                    </p>
                    <ul className="flex flex-col gap-1">
                      {prepData.previousOccurrences.map((pe) => (
                        <li key={pe.id} className="text-sm">
                          <p>{new Date(pe.startTime).toLocaleDateString()}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatEventTime(pe.startTime)} - {formatEventTime(pe.endTime)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {prepData.recentTranscripts.length > 0 && (
                  <div>
                    <p className="mb-1 text-sm font-semibold text-muted-foreground">
                      Recent Transcripts ({prepData.recentTranscripts.length})
                    </p>
                    <ul className="flex flex-col gap-1">
                      {prepData.recentTranscripts.map((t) => (
                        <li key={t.id} className="text-sm">
                          <p>{t.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {t.meetingDate}{t.aiSummary ? ` — ${t.aiSummary.substring(0, 100)}...` : ''}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {projectsIncluded && prepData.projectStatus && (
                  <div>
                    <p className="mb-1 text-sm font-semibold text-muted-foreground">
                      Project: {prepData.projectStatus.title}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      <Badge variant="outline">{prepData.projectStatus.status}</Badge>
                      <Badge variant="outline" className="border-primary text-primary">
                        {prepData.projectStatus.progressPercentage}%
                      </Badge>
                      <Badge
                        variant="outline"
                        className={ragClass(prepData.projectStatus.ragStatus)}
                      >
                        RAG: {prepData.projectStatus.ragStatus}
                      </Badge>
                    </div>
                  </div>
                )}

                {driversIncluded && prepData.driverSummary && (
                  <div>
                    <p className="mb-1 text-sm font-semibold text-muted-foreground">
                      Strategic Driver: {prepData.driverSummary.title}
                    </p>
                    <p className="mb-1 text-xs text-muted-foreground">
                      Work done this year: {formatProgress(prepData.driverSummary.progressPercentage)}
                    </p>
                    {prepData.driverSummary.kpis.length > 0 && (
                      <ul className="flex flex-col gap-1">
                        {prepData.driverSummary.kpis.map((kpi, i) => (
                          <li key={i} className="text-sm">
                            <p>{kpi.name}: {kpi.currentValue ?? '—'}{kpi.unit ? ` ${kpi.unit}` : ''}</p>
                            {kpi.targetValue != null && (
                              <p className="text-xs text-muted-foreground">
                                Target: {kpi.targetValue}{kpi.unit ? ` ${kpi.unit}` : ''}
                              </p>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {prepData.relatedWorkItems.length === 0
                  && prepData.previousOccurrences.length === 0
                  && prepData.recentTranscripts.length === 0
                  && !prepData.projectStatus
                  && !prepData.driverSummary && (
                  <p className="text-sm text-muted-foreground">
                    {driversIncluded || projectsIncluded
                      ? 'No prep context found for this meeting. Try linking a project or strategic driver.'
                      : 'No prep context found for this meeting yet.'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Meeting Brief — first-class, backend-owned prep */}
          <div className="border-t border-border pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleLoadBrief}
                disabled={briefLoading}
                className="gap-1.5"
              >
                {briefLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Meeting Brief
              </Button>
              {brief && (
                <>
                  <Badge variant="outline" className={briefStatusClass(brief.status)}>
                    {brief.status}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateBrief}
                    disabled={briefGenerating}
                    className="gap-1.5"
                  >
                    {briefGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Regenerate
                  </Button>
                  {!briefEditing && brief.status !== 'Dismissed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setBriefDraft(briefToDraft(brief)); setBriefEditing(true); setBriefExpanded(true); }}
                      className="gap-1.5"
                    >
                      <Pencil className="h-4 w-4" /> Edit
                    </Button>
                  )}
                  {brief.status !== 'Dismissed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDismissBrief}
                      disabled={briefSaving}
                      className="gap-1.5"
                    >
                      <X className="h-4 w-4" /> Dismiss
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setBriefExpanded(!briefExpanded)}
                  >
                    {briefExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </>
              )}
            </div>

            {briefError && (
              <Alert variant="error" className="mt-2">
                <AlertDescription>{briefError}</AlertDescription>
              </Alert>
            )}

            {briefExpanded && (
              <div className="mt-3 flex flex-col gap-3">
                {briefLoaded && !brief && (
                  <div className="flex flex-col items-start gap-2">
                    <p className="text-sm text-muted-foreground">No brief yet for this meeting.</p>
                    <Button size="sm" onClick={handleGenerateBrief} disabled={briefGenerating} className="gap-1.5">
                      {briefGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      Generate brief
                    </Button>
                  </div>
                )}

                {brief && (
                  <>
                    {BRIEF_SECTIONS.map(({ key, label }) => {
                      const value = brief[key] ?? '';
                      if (!briefEditing && !value) return null; // hide empty sections when reading
                      return (
                        <div key={key}>
                          <p className="mb-1 text-sm font-semibold text-muted-foreground">{label}</p>
                          {briefEditing ? (
                            <Textarea
                              rows={2}
                              value={briefDraft[key]}
                              onChange={(e) => setBriefDraft((d) => ({ ...d, [key]: e.target.value }))}
                            />
                          ) : (
                            <p className="whitespace-pre-wrap text-sm">{value}</p>
                          )}
                        </div>
                      );
                    })}

                    {briefEditing && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={handleSaveBrief} disabled={briefSaving} className="gap-1.5">
                          {briefSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setBriefEditing(false); setBriefDraft(briefToDraft(brief)); }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    {brief.linkedThreads.length > 0 && (
                      <div>
                        <p className="mb-1 text-sm font-semibold text-muted-foreground">
                          Linked threads ({brief.linkedThreads.length})
                        </p>
                        <ul className="flex flex-col gap-1">
                          {brief.linkedThreads.map((t, i) => {
                            // A brief can be pushed in via MCP, so `t.url` is
                            // attacker-influencable. Only render a clickable
                            // link when the URL passes the scheme allowlist;
                            // otherwise show inert text so a `javascript:`/
                            // `data:` payload can never run in the app origin.
                            const safeHref = isAllowedThreadUrl(t.url);
                            const occurred = t.occurredAt && (
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(t.occurredAt), 'd MMM')}
                              </span>
                            );
                            return (
                              <li key={i}>
                                {safeHref ? (
                                  <a
                                    href={t.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                                  >
                                    <ThreadIcon kind={t.kind} />
                                    <span className="truncate">{t.title || t.url}</span>
                                    <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                                    {occurred}
                                  </a>
                                ) : (
                                  <span
                                    className="flex items-center gap-1.5 text-sm text-muted-foreground"
                                    title="Link blocked (unrecognised or unsafe URL)"
                                  >
                                    <ThreadIcon kind={t.kind} />
                                    <span className="truncate">{t.title || 'Linked thread'}</span>
                                    {occurred}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      {brief.source === 'Skill' ? 'Pushed via Claude' : brief.source === 'Auto' ? 'Auto-composed' : 'Composed'}
                      {driversIncluded && brief.strategicDriverTitle ? ` · ${brief.strategicDriverTitle}` : ''}
                      {brief.modelUsed ? ` · ${brief.modelUsed}` : ''}
                      {(brief.regeneratedAt ?? brief.generatedAt)
                        ? ` · ${format(new Date(brief.regeneratedAt ?? brief.generatedAt!), 'd MMM HH:mm')}`
                        : ''}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Transcript Upload */}
          {transcriptsIncluded && (
            <div className="border-t border-border pt-3">
              <button
                type="button"
                onClick={() => setTranscriptExpanded(!transcriptExpanded)}
                className="flex w-full items-center gap-1.5 text-left"
              >
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold text-muted-foreground">Add Transcript</span>
                {transcriptExpanded
                  ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                }
              </button>
              {transcriptExpanded && (
                <div className="mt-3 flex flex-col gap-2">
                  <Textarea
                    rows={3}
                    placeholder="Paste meeting transcript or notes here..."
                    value={transcriptText}
                    onChange={(e) => setTranscriptText(e.target.value)}
                  />
                  <Button
                    size="sm"
                    onClick={handleUploadAndAnalyse}
                    disabled={transcriptUploading || !transcriptText.trim()}
                    className="gap-1.5 self-start"
                  >
                    {transcriptUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {transcriptUploading ? 'Analysing...' : 'Upload & Extract Actions'}
                  </Button>
                  {transcript?.aiSummary && (
                    <Alert>
                      <AlertDescription>
                        <p className="text-xs font-semibold">AI Summary</p>
                        <p className="text-sm">{transcript.aiSummary}</p>
                      </AlertDescription>
                    </Alert>
                  )}
                  {transcript?.extractions && transcript.extractions.length > 0 && (
                    <div>
                      <p className="mb-1 text-xs text-muted-foreground">
                        Extracted Actions ({transcript.extractions.length})
                      </p>
                      <ul className="flex flex-col gap-1">
                        {transcript.extractions.map((ext: TranscriptExtraction) => (
                          <li key={ext.id} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                            <div>
                              <p>{ext.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {ext.priority} {ext.workItemType} · {ext.suggestedDurationMinutes}m
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Quick Action Item — takes the divider when transcripts are hidden */}
          <div className={cn(!transcriptsIncluded && 'border-t border-border pt-3')}>
            <button
              type="button"
              onClick={() => setActionExpanded(!actionExpanded)}
              className="flex w-full items-center gap-1.5 text-left"
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-semibold text-muted-foreground">Quick Action Item</span>
              {actionExpanded
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />
              }
            </button>
            {actionExpanded && (
              <div className="mt-3 flex gap-2">
                <Input
                  placeholder="Action item title..."
                  value={actionTitle}
                  onChange={(e) => setActionTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && actionTitle.trim()) handleCreateAction(); }}
                />
                <Button
                  size="sm"
                  onClick={handleCreateAction}
                  disabled={actionCreating || !actionTitle.trim()}
                  className="gap-1.5 whitespace-nowrap"
                >
                  {actionCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Add
                </Button>
              </div>
            )}
          </div>

          {(feedback || transcriptFeedback) && (
            <Alert
              variant={(transcriptFeedback || feedback)!.type === 'success' ? 'success' : 'error'}
            >
              <AlertDescription>{(transcriptFeedback || feedback)!.text}</AlertDescription>
            </Alert>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
